import React, { useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

function normalizeKey(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

/**
 * Free-form note chips with soft suggestion chips filtered while typing.
 * Suggestions are hints only — any text can be committed.
 */
function NoteChipsField({
  value = [],
  onChange,
  suggestions = [],
  placeholder = "Tilføj note…",
  disabled = false,
}) {
  const [draft, setDraft] = useState("");

  const selectedKeys = useMemo(
    () => new Set(value.map((note) => normalizeKey(note))),
    [value]
  );

  const filteredSuggestions = useMemo(() => {
    const q = normalizeKey(draft);
    if (!q) return [];
    return suggestions
      .filter((opt) => {
        const key = normalizeKey(opt);
        return key && !selectedKeys.has(key) && key.includes(q);
      })
      .slice(0, 12);
  }, [draft, suggestions, selectedKeys]);

  const addNote = (raw) => {
    const note = String(raw || "").trim();
    if (!note) return;
    if (selectedKeys.has(normalizeKey(note))) {
      setDraft("");
      return;
    }
    onChange([...(value || []), note]);
    setDraft("");
  };

  const removeNote = (note) => {
    onChange(value.filter((n) => n !== note));
  };

  const onKeyDown = (event) => {
    if (event.key === "Enter" || event.key === ",") {
      event.preventDefault();
      addNote(draft);
      return;
    }
    if (event.key === "Backspace" && !draft && value.length) {
      onChange(value.slice(0, -1));
    }
  };

  return (
    <Stack spacing={1}>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 0.75,
          alignItems: "center",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          px: 1,
          py: 0.75,
          minHeight: 48,
        }}
      >
        {value.map((note) => (
          <Chip
            key={note}
            label={note}
            size="small"
            onDelete={disabled ? undefined : () => removeNote(note)}
            sx={{ borderRadius: 1 }}
          />
        ))}
        <TextField
          value={draft}
          disabled={disabled}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={value.length ? placeholder : "Skriv en note og tryk Enter"}
          variant="standard"
          InputProps={{ disableUnderline: true }}
          sx={{ flex: 1, minWidth: 140 }}
        />
      </Box>
      <Typography variant="caption" color="text.secondary">
        Frie noter — Enter eller komma låser en chip. Forslag dukker op mens du skriver.
      </Typography>
      {filteredSuggestions.length ? (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
          {filteredSuggestions.map((opt) => (
            <Chip
              key={opt}
              label={opt}
              size="small"
              variant="outlined"
              onClick={disabled ? undefined : () => addNote(opt)}
              sx={{ borderRadius: 1 }}
            />
          ))}
        </Box>
      ) : null}
    </Stack>
  );
}

export default NoteChipsField;
